import "./App.css";
import React from "react";
import Header from "./components/Header";
import Footer from "./components/Footer";
import AllEventsList from "./components/AllEventsList";
import UserRegister from "./components/UserRegister";
import UserLogin from "./components/UserLogin";
import ForgotPassword from "./components/ForgotPassword";
import ResetPasswordForm from "./components/ResetPasswordForm";
import CGU from "./components/CGU";
import MeEvents from "./components/MeEvents";
import MePeriods from "./components/MePeriods";
import CreateEvent from "./components/CreateEvent";
import CreatePeriod from "./components/CreatePeriod";
import AllPeriodsList from "./components/AllPeriodsList";
import EventDetailPage from "./components/EventDetailPage";
import PeriodDetailPage from "./components/PeriodDetailPage";
import EracheCalendarInfos from "./components/EracheCalendarInfos";
import WikifieldExtensionInfos from "./components/WikifieldExtensionInfos";
import IRLtoEracheConverter from "./components/IRLtoEracheConverter";
import EracheToIRLConverter from "./components/EracheToIRLConverter";
import CuboPricingCalculator from "./components/CuboPricingCalculator";
import Home from "./components/Home";
import AllTimelinesList from "./components/AllTimelinesList";
import MeTimelines from "./components/MeTimelines";
import UserProfile from "./components/UserProfile";
import TimelineDetail from "./components/TimelineDetail";
import CreateTimeline from "./components/CreateTimeline";
import { AppBody, AppMain, AppRoutesShell } from "./App.style";
import { BrowserRouter, Route, Routes, Navigate } from "react-router-dom";
import { AuthProvider } from "./AuthContext";
import axios from "axios";
import { ThemeProvider, createTheme } from "@mui/material/styles";

axios.defaults.withCredentials = true;

const FlowerFinder = React.lazy(() => import("./components/FlowerFinder"));

function App() {
  const getMuiMode = () => {
    const attr = document?.documentElement?.getAttribute?.("data-theme");
    return attr === "light" ? "light" : "dark";
  };

  const [muiMode, setMuiMode] = React.useState(getMuiMode);

  React.useEffect(() => {
    const el = document.documentElement;
    const obs = new MutationObserver(() => setMuiMode(getMuiMode()));
    obs.observe(el, { attributes: true, attributeFilter: ["data-theme"] });
    return () => obs.disconnect();
  }, []);

  const theme = React.useMemo(
    () =>
      createTheme({
        palette: {
          mode: muiMode,
          primary: {
            main: muiMode === "light" ? "#9b6822" : "#c8924a",
          },
          background: {
            default: muiMode === "light" ? "#f5f0e8" : "#13110e",
            paper: muiMode === "light" ? "#ede6d8" : "#1c1915",
          },
          text: {
            primary: muiMode === "light" ? "#1e1a14" : "#e8e0d0",
            secondary: muiMode === "light" ? "#6b5f4a" : "#a09080",
          },
        },
        typography: {
          fontFamily: "'Inter', 'Helvetica Neue', sans-serif",
        },
      }),
    [muiMode]
  );

  return (
    <ThemeProvider theme={theme}>
      <div className="App">
        <AuthProvider>
          <BrowserRouter>
            <AppBody>
              <Header />
              <AppMain>
                <AppRoutesShell>
                  <Routes>
                    <Route path="/" element={<Navigate to="/home" replace />} />
                    <Route path="/home" element={<Home />} />
                    <Route
                      path="/irl-to-erache-converter"
                      element={<IRLtoEracheConverter />}
                    />
                    <Route
                      path="/erache-to-irl-converter"
                      element={<EracheToIRLConverter />}
                    />
                    <Route
                      path="/outils/cubo"
                      element={<CuboPricingCalculator />}
                    />
                    <Route
                      path="/outils/fleurs"
                      element={
                        <React.Suspense fallback={null}>
                          <FlowerFinder />
                        </React.Suspense>
                      }
                    />
                    <Route path="/add-event" element={<CreateEvent />} />
                    <Route path="/edit-event/:eventId" element={<CreateEvent />} />
                    <Route path="/add-period" element={<CreatePeriod />} />
                    <Route
                      path="/edit-period/:periodId"
                      element={<CreatePeriod />}
                    />
                    <Route path="/erache-infos" element={<EracheCalendarInfos />} />
                    <Route
                      path="/extension-wikifield"
                      element={<WikifieldExtensionInfos />}
                    />
                    <Route path="/list" element={<AllEventsList />} />
                    <Route path="/events/:id" element={<EventDetailPage />} />
                    <Route path="/periods" element={<AllPeriodsList />} />
                    <Route path="/periods/:id" element={<PeriodDetailPage />} />
                    <Route path="/me/profile" element={<UserProfile />} />
                    <Route path="/me/events" element={<MeEvents />} />
                    <Route path="/me/periods" element={<MePeriods />} />
                    <Route path="/mes-frises" element={<MeTimelines />} />
                    <Route
                      path="/me/timelines"
                      element={<Navigate to="/mes-frises" replace />}
                    />
                    <Route path="/timelines" element={<AllTimelinesList />} />
                    <Route path="/timelines/:id/edit" element={<TimelineDetail />} />
                    <Route path="/timelines/:id" element={<TimelineDetail />} />
                    <Route path="/add-timeline" element={<CreateTimeline />} />
                    <Route
                      path="/edit-timeline/:timelineId"
                      element={<CreateTimeline />}
                    />
                    <Route path="/register" element={<UserRegister />} />
                    <Route path="/login" element={<UserLogin />} />
                    <Route path="/me" element={<MeEvents />} />
                    <Route path="/forgot-password" element={<ForgotPassword />} />
                    <Route
                      path="/reset-password"
                      element={<ResetPasswordForm />}
                    />
                    <Route path="/cgu" element={<CGU />} />
                    <Route path="*" element={"404"} />
                  </Routes>
                </AppRoutesShell>
              </AppMain>
              <Footer />
            </AppBody>
          </BrowserRouter>
        </AuthProvider>
      </div>
    </ThemeProvider>
  );
}

export default App;
